import { useEffect, useMemo, useRef, useState } from "react";
import { GuestUser } from "@scrile/api-provider/dist/api/GuestUsersProvider";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { MessageLinkSubjectTypeEnum } from "@scrile/api-provider/dist/api/MessageLinksProvider";
import { InputExtendedMessageCreate, Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import fileSize from "filesize";
import config from "config";
import { useTranslation } from "locales";
import useAuthorizedOnly from "hooks/useAuthorizedOnly";
import useAuthUser from "hooks/useAuthUser";
import { saveDraftText } from "hooks/useDrafts";
import useFiles from "hooks/useFiles";
import { useOnRouteChange } from "hooks/useOnRouteChange";
import emitter, { EVENTS } from "lib/emitter";
import { generateClientMessageId, sendWithRetry } from "lib/messageOutbox";
import providers from "lib/providers";
import { AttachedFileData, AttachedFileError, OptimisticMessage } from "types";

let optimisticIdCounter = 0;
function createOptimisticMessage(
  data: InputExtendedMessageCreate,
  userId: string,
  threadId?: string
): OptimisticMessage {
  const id = `_opt_${++optimisticIdCounter}_${Date.now()}`;
  return {
    id,
    text: data.text || "",
    senderId: userId,
    threadId: threadId || "",
    time: new Date().toISOString(),
    senderUser: null,
    details: null,
    link: null,
    attachment: [],
    // Carry the idempotency key so the reconcile layer can match this
    // placeholder to its real server row (which echoes the same
    // clientMessageId) across refetch/subscription and evict the placeholder
    // — even if the send response is lost. See mergeMessages / receivedMessage.
    clientMessageId: data.clientMessageId,
    _optimisticId: id,
    _status: "sending",
  };
}

type FileError = string;

const useMessageAttachments = () => {
  const { maxFileSizeUpload, maxAttachmentCount } = config;
  const { t } = useTranslation();

  const [attachedFiles, setAttachedFiles] = useState<(AttachedFileData | AttachedFileError)[]>([]);
  const { fileAddToQueue, removeFromQueue } = useFiles();
  const addAttachment = async (files: File[]) => {
    attachedFiles.forEach((f) => "error" in f && removeAttachment(f.id));
    if (files.length === 0) return;

    // Enforce the per-message file cap. Count only already-attached files that
    // aren't error cards (error cards are cleared above, but the state update
    // is async so we filter defensively).
    const currentCount = attachedFiles.filter((f) => !("error" in f)).length;
    const availableSlots = Math.max(0, maxAttachmentCount - currentCount);
    const limitExceeded = files.length > availableSlots;
    const acceptedFiles = limitExceeded ? files.slice(0, availableSlots) : files;

    const filesToUpload: (File | FileError)[] = [];
    acceptedFiles.forEach((file) => {
      if (file.size > maxFileSizeUpload) {
        filesToUpload.push(
          t("The file size may not be over {{size}}", { size: fileSize(maxFileSizeUpload, { exponent: 3 }) })
        );
        return;
      }
      filesToUpload.push(file);
    });
    // Surface the cap as an error card (same mechanism as oversize files).
    if (limitExceeded) {
      filesToUpload.push(t("You can attach up to {{max}} files", { max: maxAttachmentCount }));
    }
    const updateFile = (id: string, data: Partial<AttachedFileData>) => {
      setAttachedFiles((files) => {
        return files.map((f) => {
          if (f.id === id) {
            return {
              ...f,
              ...data,
            };
          }
          return f;
        });
      });
      return data.id ?? id;
    };
    const addAttachmentError = (attachedFileError: AttachedFileError) => {
      setAttachedFiles((files) => [...files, attachedFileError]);
    };
    for (const file of filesToUpload) {
      let fileIndex = Math.random().toString(36).substring(2, 15) + filesToUpload.indexOf(file);
      if (file instanceof File) {
        setAttachedFiles((files) => [
          ...files,
          {
            id: fileIndex,
            fileName: file.name,
            mediaType: getFileMimeType(file),
            size: file.size,
            uploadProgress: 0,
          },
        ]);
        const {
          uploadData: { uuid },
          uploadId,
        } = await fileAddToQueue({
          type: "message_attachment",
          file: file,
          onSuccess: async () => {
            fileIndex = updateFile(fileIndex, { id: uuid, uploadProgress: 100 });
          },
          onUploadProgress: (p) => {
            const uploadProgress = Math.floor(((p.loaded ?? 0) / (p.total ?? 1)) * 100);
            const id = uploadProgress < 100 ? uploadId : uuid;
            fileIndex = updateFile(fileIndex, { uploadProgress, id });
          },
          onError: (error) => {
            // eslint-disable-next-line no-console
            console.debug(`File uploading error: ${error.message}`);
            removeAttachment(fileIndex);
            // Map HTTP 413 (payload too large — nginx client_max_body_size) to a
            // friendly, actionable message instead of a generic "Uploading error".
            const raw = `${(error as any)?.status ?? ""} ${error?.message ?? ""}`;
            const tooLarge = /\b413\b|too large|payload too large|entity too large/i.test(raw);
            addAttachmentError({
              id: fileIndex,
              error: tooLarge ? t("This file is too large to upload.") : t("Uploading error"),
            });
          },
        });
        fileIndex = updateFile(fileIndex, { id: uploadId });
      } else {
        addAttachmentError({
          id: fileIndex,
          error: file,
        });
      }
    }
  };

  const removeAttachment = (id: string) => {
    setAttachedFiles((files) => {
      const fileData = files.find((f) => f.id === id);
      if (!fileData) return files;
      if ("uploadProgress" in fileData && fileData.uploadProgress < 100) {
        removeFromQueue(fileData.id);
      }
      return files.filter((f) => f.id !== id);
    });
  };

  const isUploading = useMemo(
    () => attachedFiles.some((i) => "uploadProgress" in i && i.uploadProgress < 100),
    [attachedFiles]
  );

  return {
    attachedFiles,
    addAttachment,
    removeAttachment,
    isUploading,
  };
};

// Defense-in-depth against system-message spoofing from the composer: the
// client renders a leading `__SYSTEM__:`/`__META__:` as an authorless system
// card (and the backend suppresses its notifications). App-generated system
// messages are posted through the providers directly (GroupInfoPanel,
// PageJoinGroup, call/recording flows) — never through a composer path — so a
// marker typed by the user is always an impersonation attempt. Break the
// prefix match with a zero-width space; the visible text is unchanged.
// Module-scope + exported: both the normal send (buildMessageData) and the
// scheduled send (handleScheduleSend, which builds its own reply envelope and
// bypasses buildMessageData) must neutralize BEFORE any legit envelope is
// prepended. NOTE: only closes the in-UI vector — a crafted API call can still
// author raw system text; fully closing it needs server-side authorship.
export const neutralizeSystemPrefix = (text: string): string =>
  text.replace(/^(\s*__(?:SYSTEM|META)__):/, "$1​:");

function useController(
  recipientIds?: string[],
  threadIds?: string[],
  allowGuestMessage?: boolean,
  guestUser?: GuestUser | null,
  setGuestUser?: (guest: GuestUser) => void,
  me?: LiveChatUser
) {
  const { user } = useAuthUser();
  const [value, setValue] = useState("");
  const [textArea, setTextarea] = useState<HTMLTextAreaElement | null>(null);
  const sending = useRef(false);
  const { attachedFiles, addAttachment, removeAttachment, isUploading } = useMessageAttachments();
  const { t } = useTranslation();

  // Live snapshot of the attachments. The queued-send path awaits uploads
  // across many renders, so anything it reads afterwards (file list, upload
  // state) must come from a ref, not from a render-time closure.
  const attachedFilesRef = useRef(attachedFiles);
  attachedFilesRef.current = attachedFiles;

  // Live thread identity + mount flag: the queued-send abort path decides
  // between restoring the composer (same chat still on screen) and stashing
  // the captured text as the ORIGIN thread's draft (chat switched / unmount).
  const threadIdsRef = useRef(threadIds);
  threadIdsRef.current = threadIds;
  const mountedRef = useRef(true);

  // A send requested while uploads were still running: it fires
  // automatically once every attachment reaches 100% (Telegram behaviour) —
  // previously the message went out with only the files that had finished,
  // silently dropping the rest.
  const queuedSendRef = useRef(false);
  // Set on route change: the queued send must NOT fire into the previous
  // chat after its attachments were cleared (and must not clobber the new
  // chat's composer).
  const queueAbortRef = useRef(false);
  const [sendQueued, setSendQueued] = useState(false);

  useEffect(
    () => () => {
      // Unmount without a route event (e.g. layout toggles remount the
      // composer): stop the queued-send poll so it doesn't spin for 120s
      // and fire an orphaned alert.
      mountedRef.current = false;
      queueAbortRef.current = true;
    },
    []
  );

  // Canonical recorder uploads (round-video / voice) ride the attachedFiles
  // pipeline for upload/progress UI but are sent through their own
  // sendFileWithMeta flow — the queued text send must ignore them entirely
  // (including them broke the video-send effect chain and left the composer
  // stuck on the "Sending video message..." spinner).
  // KNOWN LIMITATION: matching by canonical filename means a user re-attaching
  // a previously-downloaded "voice-message.webm" from disk is excluded from
  // regular sends too. The whole recorder pipeline is name-keyed (see the
  // video-send effect in index.tsx), so this heuristic matches its contract.
  const isRecorderFile = (f: AttachedFileData | AttachedFileError) =>
    "fileName" in f && (f.fileName === "video-message.webm" || f.fileName === "voice-message.webm");

  const stillUploading = () =>
    attachedFilesRef.current.some((f) => !isRecorderFile(f) && "uploadProgress" in f && f.uploadProgress < 100);

  /**
   * Wait until every regular attachment finished uploading AND no other send
   * holds `sending.current` (a GIF / voice send interleaving with the queued
   * wait would otherwise make the queued sendMessage early-return silently).
   * Resolves true when clear to send; false on timeout or route-change abort.
   */
  const waitForUploads = async (timeoutMs = 120000): Promise<boolean> => {
    const startedAt = Date.now();
    while ((stillUploading() || sending.current) && !queueAbortRef.current) {
      if (Date.now() - startedAt > timeoutMs) return false;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 200));
    }
    return !queueAbortRef.current;
  };

  const removeAllAttachments = () => attachedFilesRef.current.forEach((f) => removeAttachment(f.id));
  const removeAllAttachmentsOnSend = () =>
    attachedFilesRef.current.forEach((f) => {
      if (isRecorderFile(f)) return; // owned by the voice/video send pipeline
      if ("uploadProgress" in f) {
        if (f.uploadProgress === 100) removeAttachment(f.id);
      } else {
        removeAttachment(f.id);
      }
    });

  /** Returns true when a send was actually attempted (optimistic emitted /
   *  network fired); false when the guard early-returned (busy or empty). */
  const sendMessage = async (data: InputExtendedMessageCreate): Promise<boolean> => {
    // Allow a text-less send only when there's at least one REAL attachment
    // (error cards and recorder files don't count) and nothing is still
    // uploading — otherwise a composer holding only an error card would post
    // a blank message.
    // Read through the ref: after a queued send the render-time closure is stale.
    const allowEmptyMessage =
      attachedFilesRef.current.some((f) => !("error" in f) && !isRecorderFile(f)) && !stillUploading();
    if (sending.current || (!data.text.trim() && !allowEmptyMessage)) return false;
    textArea?.focus();
    sending.current = true;

    // Optimistic: show message instantly
    const threadId = threadIds?.[0];
    const optimistic = user ? createOptimisticMessage(data, user.id, threadId) : null;
    if (optimistic) {
      emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, optimistic);
    }
    // NOTE: the composer text is cleared by queueSend at CLICK time (so text
    // typed during the upload wait survives) — not here.
    // A file-less send fired while a queued send waits (GIF via sendRawText)
    // must not strip the attachments that queued send is waiting for.
    if (!queuedSendRef.current || (data.files && data.files.length > 0)) {
      removeAllAttachmentsOnSend();
    } else {
      // Still drop stale error cards on a text-only send.
      attachedFilesRef.current.forEach((f) => {
        if ("error" in f) removeAttachment(f.id);
      });
    }

    try {
      // sendWithRetry retries with the SAME clientMessageId on transient
      // failures (network blip, lost response). Server-side dedup keyed
      // on (sender, thread, clientMessageId) absorbs the retry without
      // producing a duplicate. If retries are exhausted, the optimistic
      // message is marked failed exactly as before.
      const result = data.clientMessageId
        ? await sendWithRetry(data.clientMessageId, () => providers.MessagesProvider.sendExtendedMessages({ data }))
        : await providers.MessagesProvider.sendExtendedMessages({ data });
      if (result && result.length > 0) {
        // Replace optimistic message with real one
        if (optimistic) {
          emitter.emit(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, {
            optimisticId: optimistic._optimisticId,
            message: result[0],
            status: "sent",
          });
        }
        // Emit remaining messages normally
        result.slice(optimistic ? 1 : 0).forEach((msg) => emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, msg));
      } else if (optimistic) {
        // Empty result without an exception (degenerate dedup/transport case).
        // Never leave the placeholder stuck on "sending" — mark it failed. If
        // the server actually persisted the message, its real twin arrives via
        // subscription/refetch carrying the same clientMessageId and the
        // reconcile-by-clientMessageId path replaces this placeholder.
        emitter.emit(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, {
          optimisticId: optimistic._optimisticId,
          message: null,
          status: "failed",
        });
      }
      return true;
    } catch (e) {
      // Mark optimistic message as failed
      if (optimistic) {
        emitter.emit(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, {
          optimisticId: optimistic._optimisticId,
          message: null,
          status: "failed",
        });
      }
      throw e;
    } finally {
      sending.current = false;
    }
  };

  const buildMessageData = (textOverride?: string): InputExtendedMessageCreate => ({
    // The queued path captures the text at click time — `value` may have
    // changed by the time uploads finish.
    text: neutralizeSystemPrefix((textOverride ?? value).trim()),
    threadIds: threadIds || [],
    // recipientIds and threadIds are mutually exclusive on the backend:
    // when a thread exists the message is posted TO the thread. Groups/channels
    // pass their member ids as `userIds` (for @-mention autocomplete), which
    // used to double as recipientIds and fan the message out to every member as
    // individual DMs. Only send recipientIds when there is no thread yet (a
    // brand-new 1-on-1 that the backend find-or-creates).
    recipientIds: threadIds && threadIds.length > 0 ? [] : recipientIds || [],
    // Read through the ref so files that finished uploading DURING the
    // queued wait are included. Recorder files (voice/round-video) are sent
    // through their own pipeline and never as plain attachments.
    files: attachedFilesRef.current
      .filter((f) => !isRecorderFile(f) && "uploadProgress" in f && f.uploadProgress === 100)
      .map((f) => ({ publicAccess: true, uuid: f.id })),
    pricePackageId: "0",
    // UUID generated once per send so server-side dedup can collapse
    // retries (network blip, manual user-tap-again) without producing
    // duplicate messages.
    clientMessageId: generateClientMessageId(),
  });

  // Wait for in-flight uploads, then build + send. Double-clicks while
  // queued are ignored; the send button reflects `sendQueued`. The composer
  // is cleared at CLICK time (the captured text is what gets sent), so text
  // typed during the wait is preserved.
  const queueSend = async (
    capturedText: string,
    buildData: () => InputExtendedMessageCreate | Promise<InputExtendedMessageCreate>
  ) => {
    if (queuedSendRef.current) return;
    queuedSendRef.current = true;
    queueAbortRef.current = false;
    setSendQueued(true);
    const originThreadId = threadIdsRef.current?.[0];
    // Non-recorder attachments this send is waiting on. If any of them FAILS
    // during the wait (e.g. 413), it stops counting as "still uploading" and
    // becomes an error card — without this snapshot the send would fire with
    // only the survivors and removeAllAttachmentsOnSend would even delete the
    // error card, silently dropping the file.
    const queuedAttachmentIds = attachedFilesRef.current
      .filter((f) => !isRecorderFile(f))
      .map((f) => f.id);
    setValue((v) => (v === capturedText ? "" : v));
    // The captured text must never be silently lost: restore it into the
    // composer when this chat is still on screen, otherwise stash it as the
    // ORIGIN thread's draft (never clobbers the new chat's composer). If the
    // user typed something new during the wait, keep BOTH (captured above the
    // new text) rather than dropping the captured text.
    const preserveText = () => {
      if (!capturedText.trim()) return;
      if (mountedRef.current && (!originThreadId || threadIdsRef.current?.[0] === originThreadId)) {
        setValue((v) => {
          if (!v.trim()) return capturedText;
          if (v.includes(capturedText)) return v;
          return `${capturedText}\n${v}`;
        });
      } else if (originThreadId) {
        saveDraftText(originThreadId, capturedText);
      }
    };
    // True if any attachment we were waiting on vanished or turned into an
    // error card while queued — sending now would post a partial message.
    const anyQueuedAttachmentFailed = () =>
      queuedAttachmentIds.some((id) => {
        const f = attachedFilesRef.current.find((x) => x.id === id);
        return !f || "error" in f;
      });
    try {
      const clearToSend = await waitForUploads();
      if (!clearToSend) {
        preserveText();
        if (!queueAbortRef.current && mountedRef.current) {
          // Timeout, same chat: nothing was sent — say so.
          // eslint-disable-next-line no-alert
          window.alert(t("Files are still uploading — the message was not sent."));
        }
        return;
      }
      if (anyQueuedAttachmentFailed()) {
        // Abort rather than send a partial message; keep the error card(s) and
        // the text so the user can retry after removing the failed file.
        preserveText();
        if (mountedRef.current) {
          // eslint-disable-next-line no-alert
          window.alert(t("Some files failed to upload — the message was not sent."));
        }
        return;
      }
      const sent = await sendMessage(await buildData());
      // Guard early-return (busy / nothing to send): don't eat the text.
      if (!sent) preserveText();
    } catch (e) {
      preserveText();
      throw e;
    } finally {
      queuedSendRef.current = false;
      setSendQueued(false);
    }
  };

  const sendWithMetadata = async (metadata: Record<string, any>) => {
    const text = value;
    await queueSend(text, () => {
      const data = buildMessageData(text);
      const metaPrefix = `__META__:${JSON.stringify(metadata)}\n`;
      data.text = metaPrefix + data.text;
      return data;
    });
  };

  // GIF picker: a standalone message; the composer draft is untouched
  // (sendMessage doesn't clear the textarea — queueSend owns that).
  const sendRawText = async (text: string) => {
    if (sending.current) return;
    const data: InputExtendedMessageCreate = {
      text,
      threadIds: threadIds || [],
      recipientIds: recipientIds || [],
      files: [],
      pricePackageId: "0",
      clientMessageId: generateClientMessageId(),
    };
    await sendMessage(data);
  };

  const scheduleMessage = async (threadId: string, text: string, scheduledAt: string) => {
    await providers.MessagesProvider.scheduleMessage({ threadId, text, scheduledAt });
  };

  const transcribeUploadedFile = async (fileUuid: string): Promise<string> => {
    return (providers as any).MessagesProvider.transcribeUploadedFile({ fileUuid });
  };

  const sendFileWithMeta = async (fileUuid: string, metadata: Record<string, any>) => {
    const data: InputExtendedMessageCreate = {
      text: `__META__:${JSON.stringify(metadata)}\n`,
      threadIds: threadIds || [],
      recipientIds: recipientIds || [],
      files: [{ publicAccess: true, uuid: fileUuid }],
      pricePackageId: "0",
      clientMessageId: generateClientMessageId(),
    };
    // A queued text send can hold `sending.current` for its network RTT
    // exactly when the recorder pipeline fires — a silent early-return here
    // deleted the recording unsent (the callers' success paths remove the
    // file). Wait out the contention (bounded) and THROW on failure so the
    // callers' catch paths keep the file / surface an error.
    const contentionStart = Date.now();
    while (sending.current && Date.now() - contentionStart < 15000) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 150));
    }
    if (sending.current) throw new Error("Another send is in progress");
    textArea?.focus();
    sending.current = true;
    try {
      // Route through sendWithRetry so voice/video file sends get the same
      // retry resilience and server-side idempotency dedup (keyed on the
      // clientMessageId above) as text messages — a lost response no longer
      // risks a duplicate on retry.
      const result = data.clientMessageId
        ? await sendWithRetry(data.clientMessageId, () => providers.MessagesProvider.sendExtendedMessages({ data }))
        : await providers.MessagesProvider.sendExtendedMessages({ data });
      if (result && result.length > 0) {
        result.forEach((msg) => emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, msg));
      }
    } finally {
      sending.current = false;
    }
  };

  const onSendMessageGuest = async () => {
    if (!me) return;
    const text = value;
    await queueSend(text, async () => {
      // buildMessageData already carries the idempotency clientMessageId so
      // guest sends get the same retry/dedup behaviour as authorized ones.
      const data = buildMessageData(text);
      if (!guestUser) {
        const guest = await providers.GuestUsersProvider.create({ data: { screenName: me.screenName } });
        setGuestUser && setGuestUser(guest);
        data.linkSubject = { subjectId: guest.id, subjectType: MessageLinkSubjectTypeEnum.GUEST_USER };
      } else {
        data.linkSubject = { subjectId: guestUser.id, subjectType: MessageLinkSubjectTypeEnum.GUEST_USER };
      }
      return data;
    });
  };

  const onSendMessageAuthorized = useAuthorizedOnly(
    async () => {
      const text = value;
      await queueSend(text, () => buildMessageData(text));
    },
    {
      notification: t("Log in or register to send a message"),
    }
  );

  // Do NOT clear `value` here — drafts are loaded/saved by MessageInput keyed on
  // threadId and a forced reset would overwrite the draft for the outgoing thread.
  // Attachments are still discarded since uploads are thread-scoped.
  useOnRouteChange(() => {
    // Abort a queued send: its files are being cleared right here, and firing
    // it later would post a file-less message into the PREVIOUS chat.
    queueAbortRef.current = true;
    removeAllAttachments();
  });

  const addAttachmentWrapper = (files: File[]) => {
    textArea?.focus();
    return addAttachment(files);
  };

  return {
    user,
    value,
    setValue,
    onSendMessage: allowGuestMessage && !user ? onSendMessageGuest : onSendMessageAuthorized,
    sendWithMetadata,
    sendRawText,
    sendFileWithMeta,
    transcribeUploadedFile,
    scheduleMessage,
    setTextarea,
    attachedFiles,
    isUploading,
    sendQueued,
    addAttachment: addAttachmentWrapper,
    removeAllAttachments,
    removeAttachment,
  };
}

export default useController;
